import deleteCompanyProductApi from '@/api/delete/company/delete-company-product';
import getCompanyProductsApi from '@/api/get/company/company-product/get-company-products';
import getCompanyTemplatesSelectApi from '@/api/get/company/company-template/get-company-templates-select';
import Alert from '@/components/alert/alert-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import ViewEditDelete from '@/components/table/components/view-edit-delete/view-edit-delete';
import Table from '@/components/table/table';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Modal from '@/components/modal/modal-component';
import { MdOutlineDragIndicator } from 'react-icons/md';
import Styled from './products-all-style';
import NewProductModal from './create-product-modal';
import ReorderProducts from './reorder-products/reorder-products';
/**
 * Table of company products
 * @returns {JSX.Element}
 */
const CompanyProducts = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  window.history.replaceState({}, '');
  const { templateId } = useLocation().state || {};
  const tableId = 'company-product-list';
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [defaultVariables, setDefaultVariables] = useState(
    templateId
      ? {
          filter: { template_id: { value: templateId, type: '$eq' } },
        }
      : {},
  );
  const [defaultSort, setDefaultSort] = useState('product.created_at:DESC');
  const [templateOptions, setTemplateOptions] = useState([]);
  const [templateLoading, setTemplateLoading] = useState(true);
  const [openNewProductModal, setOpenNewProductModal] = useState(false);
  const [openReorderProductModal, setOpenReorderProductModal] = useState(false);

  const renderStatusPill = useCallback(({ row }) => {
    const { status } = row.original;
    return <StatusIndicator status={status} />;
  }, []);

  const renderDate = useCallback(({ row }) => {
    if (row.original) {
      return (
        <>
          Modified {dayjs(row.original.updated_at).format('YYYY/MM/DD HH:mm')}
        </>
      );
    }
  }, []);

  const [deleteCompanyProduct, { loading: deleteCompanyProductLoading }] =
    useMutation(deleteCompanyProductApi);

  const getApi = useRef(getCompanyProductsApi(id));

  useEffect(() => {
    getCompanyTemplatesSelectApi(id).then((response) => {
      const templates = response.data.map((d) => ({
        value: d.id,
        text: d.name,
      }));
      setTemplateOptions(templates);
      setTemplateLoading(false);
    });
  }, [id]);

  const {
    data,
    count,
    error,
    loading: isLoading,
    refetch,
  } = useLazyQuery(getApi.current);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;

        if (query?.length > 0) {
          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery?.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            setDefaultVariables({ ...defaultVariables, search: tempSearch });
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery?.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');
            setDefaultSort(tempSort);
          }

          // Get cached page size and apply it
          const pageSizeQuery = query?.filter((q) => q.includes('limit='));
          if (pageSizeQuery?.length > 0) {
            const tempPageSize = parseInt(
              pageSizeQuery[0].replace('limit=', ''),
              10,
            );
            setDefaultVariables({ ...defaultVariables, limit: tempPageSize });
          }
        }
      }
    }
    setFetchingPresets(false);

    // This is disabled because we don't want to re-run this function when the defaultVariables changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  const renderThumbnail = useCallback(
    ({ row }) =>
      row?.original?.thumbnail ? (
        <Styled.ImageContainer>
          <Styled.Thumbnail src={row?.original?.thumbnail} alt="thumbnail" />
        </Styled.ImageContainer>
      ) : (
        <Styled.IconContainer>
          <Styled.NoThumbnail />
        </Styled.IconContainer>
      ),
    [],
  );

  const launchDeleteConfirmation = useCallback(
    (ProductData) => {
      ConfirmationModal({
        title: 'Delete Option',
        text: `Are you sure you want to delete ${ProductData.values.name}?`,
        onConfirm: () =>
          deleteCompanyProduct(id, ProductData?.original?.id).then(() => {
            let query = '';
            if (templateId) {
              query = `filter=template_id$eq=${templateId}`;
            }
            refetch(query).then(() => {
              Alert({
                icon: 'success',
                text: 'Product deleted successfully',
              });
            });
          }),
      });
    },
    [deleteCompanyProduct, id, refetch, templateId],
  );

  const handleEdit = useCallback(
    (row) => {
      const productDetails = row.original;
      navigate(`edit/${productDetails.id}`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (row) => {
      launchDeleteConfirmation(row);
    },
    [launchDeleteConfirmation],
  );

  const handlePreview = useCallback(
    (row) => {
      const productDetails = row.original;

      navigate(`edit/${productDetails.id}/preview`, {
        state: { fromTable: true },
      });
    },
    [navigate],
  );

  const handleDuplicate = useCallback(
    (row) => {
      const productDetails = row.original;

      navigate(`new`, {
        state: { isProductDuplicate: productDetails.id },
      });
    },
    [navigate],
  );

  const Actions = useCallback(
    ({ row }) => (
      <ViewEditDelete
        feature="product"
        onEdit={() => handleEdit(row)}
        onDelete={() => handleDelete(row)}
        viewText="Preview"
        onView={() => handlePreview(row)}
        onDuplicate={() => handleDuplicate(row)}
      />
    ),
    [handleDelete, handleDuplicate, handleEdit, handlePreview],
  );

  const ProductColumns = [
    {
      Header: 'Thumbnail',
      id: 'thumbnail',
      width: 100,
      accessor: 'thumbnail',
      Cell: (props) => renderThumbnail(props),
    },
    {
      Header: 'NAME',
      id: 'name',
      accessor: 'name',
      search: true,
    },
    {
      Header: 'PART NUMBER',
      id: 'part_number',
      accessor: 'part_number',
      search: true,
    },
    {
      Header: 'TEMPLATE NAME',
      id: 'template_name',
      accessor: 'template_name',
      search: true,
    },
    {
      Header: 'CATEGORY',
      id: 'categories',
      accessor: 'categories',
      search: true,
    },
    {
      Header: 'PRODUCT CATEGORY',
      id: 'product_category',
      accessor: 'product_category',
      search: true,
    },
    {
      Header: 'DATE',
      id: 'updated_at',
      accessor: 'updated_at',
      search: true,
      Cell: (props) => renderDate(props),
    },
    {
      Header: 'STATUS',
      id: 'status',
      accessor: 'status',
      search: true,
      Cell: (props) => renderStatusPill(props),
    },
    {
      Header: 'ACTIONS',
      id: 'actions',
      Cell: (props) => Actions(props),
    },
  ];

  const filters = [
    {
      header: 'Status:',
      accessor: 'status',
      placeholder: 'Please select a Status',
      type: 'select',
      options: [
        { value: 'active', text: 'Active' },
        { value: 'invalid', text: 'Invalid' },
        { value: 'draft', text: 'Draft' },
      ],
    },
    {
      header: 'Template:',
      accessor: 'template_id',
      placeholder: 'Please select a Template',
      type: 'select',
      options: templateOptions,
    },
  ];

  return (
    <>
      <Table
        feature="product"
        onRowEdit={(row) => handleEdit(row)}
        onRowDelete={(row) => handleDelete(row)}
        onRowDuplicate={(row) => handleDuplicate(row)}
        onRowPreview={(row) => handlePreview(row)}
        previewText="Preview"
        columns={ProductColumns}
        data={data ?? []}
        tableOptions={['search', 'columns', 'filter']}
        id={tableId}
        defaultSort={defaultSort}
        dataCount={count ?? 0}
        downloadAccessor="product-list"
        search
        defaultVariables={defaultVariables}
        clearDefaultOnReset
        error={error}
        loading={
          deleteCompanyProductLoading ||
          isLoading ||
          templateLoading ||
          fetchingPresets
        }
        refetch={refetch}
        filters={filters}
        tableHeaderProps={
          <PermissionWrapper permissions={['product.create']}>
            <Styled.HeaderButtonStyled
              title="Add Product"
              buttonHeight="40px"
              buttonWidth="124px"
              cy="btnCreateProduct"
              onClick={() => {
                setOpenNewProductModal(true);
              }}
            />
          </PermissionWrapper>
        }
        additionalHeaderButtons={[
          {
            title: 'Reorder Product',
            icon: MdOutlineDragIndicator,
            onClick: () => {
              setOpenReorderProductModal(true);
            },
          },
        ]}
        onRowContextMenu={(row) => Actions(row)}
      />
      <Modal
        width="500px"
        open={openNewProductModal}
        height="auto"
        showClose
        bodyStyle={{
          padding: '0px',
        }}
        close={() => {
          setOpenNewProductModal(false);
        }}
        title="New Product"
      >
        <NewProductModal
          open={openNewProductModal}
          onClose={() => {
            setOpenNewProductModal(false);
          }}
        />
      </Modal>
      <Modal
        width="75vw"
        open={openReorderProductModal}
        minHeight="75vh"
        titleFontSize="32px"
        showClose={false}
        bodyStyle={{
          padding: '0px 32px',
        }}
        headerPadding="32px 17px"
        close={() => {
          setOpenReorderProductModal(false);
        }}
        title="Reorder Product"
        createPortal="reorderProductPortal"
      >
        <ReorderProducts
          open={openReorderProductModal}
          onClose={() => {
            setOpenReorderProductModal(false);
          }}
        />
      </Modal>
    </>
  );
};

CompanyProducts.defaultProps = {};

CompanyProducts.propTypes = {};

export default CompanyProducts;
